import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Eye, 
  Mail,
  Loader2
} from 'lucide-react';
import type { Template } from '@/types/template';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { TemplateVariablesPanel } from '@/components/template-variables-panel';
import type { Editor } from '@tiptap/react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { VariableExtension } from '@/lib/tiptap-variable-extension';
import { mapTemplateDetailApiToUi } from '@/lib/mappers/templates';
import { useTemplateById, useUpdateTemplate, useCreateTemplate } from '@/hooks/useTemplatesData';

// Helper function to convert plain text variables to variable nodes
const convertVariablesToNodes = (html: string) => {
  const variableRegex = /\{\{([^}]+)\}\}/g;
  return html.replace(variableRegex, (match) => {
    return `<span data-variable="${match}" class="variable-node"></span>`;
  });
};

const convertVariableNodesToText = (html: string): string => {
  return html
    .replace(/<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g, '$1')
    .replace(/<\/?p>/g, '')
    .trim();
};

export function TemplateEditorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationTemplate = location.state?.template as Template | undefined;
  const isEditMode = location.state?.mode === 'edit';
  const duplicateFromId = location.state?.duplicateFromId as string | undefined;
  const isDuplicateMode = !!duplicateFromId;
  const fetchId = isEditMode ? (locationTemplate?.id ?? null) : (duplicateFromId ?? null);

  const { data: fetchedTemplate, isLoading: isLoadingTemplate } = useTemplateById(fetchId);
  const template = (() => {
    if (isEditMode) return fetchedTemplate ?? locationTemplate;
    if (isDuplicateMode && fetchedTemplate) {
      return { ...fetchedTemplate, name: locationTemplate?.name ?? `${fetchedTemplate.name} (COPY)` };
    }
    return locationTemplate;
  })();
  const updateTemplateMutation = useUpdateTemplate(fetchId ?? '');
  const createTemplateMutation = useCreateTemplate();
  const [isSaving, setIsSaving] = useState(false);

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const contentEditorRef = useRef<Editor | null>(null);
  const [type] = useState<'email' | 'statement'>(template?.type || 'email');
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [lastFocusedEditor, setLastFocusedEditor] = useState<'subject' | 'content'>('content');
  const [subjectCursorPosition, setSubjectCursorPosition] = useState<number | null>(null);
  const [contentCursorPosition, setContentCursorPosition] = useState<number | null>(null);
  const [attachInvoiceCopies, setAttachInvoiceCopies] = useState(false);
  const [isSubjectDragOver, setIsSubjectDragOver] = useState(false);
  
  // Store initial values to detect changes
  const [initialName, setInitialName] = useState('');
  const [initialSubject, setInitialSubject] = useState('');
  const [initialContent, setInitialContent] = useState('');
  const [initialAttachInvoiceCopies, setInitialAttachInvoiceCopies] = useState(false);
  
  // Cancel confirmation dialog
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const pendingContentRef = useRef<string>('');
  const initializedFromRef = useRef<Template | null>(null);

  const handleContentEditorReady = useCallback((editor: Editor) => {
    contentEditorRef.current = editor;
    if (pendingContentRef.current) {
      editor.commands.setContent(pendingContentRef.current);
    }
  }, []);

  const handleContentEditorFocus = useCallback(() => {
    setLastFocusedEditor('content');
  }, []);

  const handleContentSelectionUpdate = useCallback((position: number) => {
    setContentCursorPosition(position);
  }, []);

  const handleContentEditorBlur = useCallback((position: number) => {
    setContentCursorPosition(position);
  }, []);

  // Subject editor (single line)
  const subjectEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: 'Enter email subject...',
      }),
      VariableExtension,
    ],
    content: subject,
    editorProps: {
      handleDrop: (view, event, slice, moved) => {
        // Check if this is a template variable drop
        const variable = event.dataTransfer?.getData('application/x-template-variable');
        if (variable) {
          event.preventDefault();
          event.stopPropagation();
          
          // Clear the drag over state immediately
          setIsSubjectDragOver(false);
          
          console.log('Subject editor - dropping variable:', variable);
          
          // Get the position where the drop occurred
          const coords = { left: event.clientX, top: event.clientY };
          const pos = view.posAtCoords(coords);
          
          console.log('Subject editor - drop position:', pos);
          
          if (pos) {
            // Get the editor instance from the view
            const { state, dispatch } = view;
            
            // Create the variable node
            const variableNode = state.schema.nodes.variable.create({ variable });
            
            // Insert at the drop position
            const transaction = state.tr.insert(pos.pos, variableNode);
            dispatch(transaction);
            
            console.log('Subject editor - variable inserted at position:', pos.pos);
          }
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        dragover: (view, event) => {
          // Check if this is a template variable
          const types = event.dataTransfer?.types || [];
          if (types.includes('application/x-template-variable')) {
            event.preventDefault();
            event.dataTransfer!.dropEffect = 'copy';
            return true;
          }
          return false;
        },
      },
    },
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      setSubject(text);
    },
    onFocus: () => {
      setLastFocusedEditor('subject');
    },
    onSelectionUpdate: ({ editor }) => {
      // Store cursor position whenever it changes
      const pos = editor.state.selection.from;
      setSubjectCursorPosition(pos);
    },
    onBlur: ({ editor }) => {
      // Store cursor position when editor loses focus
      const pos = editor.state.selection.from;
      setSubjectCursorPosition(pos);
    },
  });

  useEffect(() => {
    if (isEditMode && !locationTemplate && !isLoadingTemplate && !fetchedTemplate) {
      navigate('/settings/templates');
    }
  }, [isEditMode, locationTemplate, isLoadingTemplate, fetchedTemplate, navigate]);

  useEffect(() => {
    const sourceTemplate = (isEditMode || isDuplicateMode) ? fetchedTemplate : locationTemplate;
    if (!sourceTemplate) return;
    if ((isEditMode || isDuplicateMode) && !fetchedTemplate) return;
    if (sourceTemplate === initializedFromRef.current) return;

    const isReinit = initializedFromRef.current !== null;
    initializedFromRef.current = sourceTemplate;

    const convertedSubject = convertVariablesToNodes(sourceTemplate.subject || '');
    const convertedContent = convertVariablesToNodes(sourceTemplate.content);

    const effectiveName = isDuplicateMode
      ? (locationTemplate?.name ?? `${sourceTemplate.name} (COPY)`)
      : sourceTemplate.name;

    setName(effectiveName);
    setSubject(convertedSubject);
    setContent(convertedContent);
    setAttachInvoiceCopies(sourceTemplate.attachInvoiceCopies || false);

    setInitialName(effectiveName);
    setInitialSubject(convertedSubject);
    setInitialContent(convertedContent);
    setInitialAttachInvoiceCopies(sourceTemplate.attachInvoiceCopies || false);

    pendingContentRef.current = convertedContent;

    if (isReinit) {
      if (contentEditorRef.current && !contentEditorRef.current.isDestroyed) {
        contentEditorRef.current.commands.setContent(convertedContent);
      }
      if (subjectEditor && !subjectEditor.isDestroyed) {
        subjectEditor.commands.setContent(convertedSubject);
      }
    }
  }, [fetchedTemplate, locationTemplate, isEditMode, isDuplicateMode, subjectEditor]);

  useEffect(() => {
    if (!subjectEditor || subjectEditor.isDestroyed || !initializedFromRef.current) return;
    if (subjectEditor.getHTML() === '<p></p>' && subject) {
      subjectEditor.commands.setContent(subject);
    }
  }, [subjectEditor, subject]);

  const handleInsertVariable = (variable: string) => {
    // Insert into the last focused editor
    if (lastFocusedEditor === 'subject' && subjectEditor) {
      try {
        // Check if editor is available and not destroyed
        if (!subjectEditor.view || subjectEditor.isDestroyed) {
          return;
        }
        
        // Focus the editor first
        subjectEditor.commands.focus();
        
        // If we have a stored cursor position, move to it
        if (subjectCursorPosition !== null) {
          subjectEditor.commands.setTextSelection(subjectCursorPosition);
        }
        
        // Use the custom insertVariable command
        (subjectEditor.commands as any).insertVariable(variable);
      } catch (error) {
        console.error('Error inserting into subject editor:', error);
      }
    } else {
      // For content editor, try to use the ref if available, otherwise update via state
      if (contentEditorRef.current && contentEditorRef.current.view && !contentEditorRef.current.isDestroyed) {
        const editor = contentEditorRef.current;
        
        try {
          // Focus the editor first
          editor.commands.focus();
          
          // If we have a stored cursor position, move to it
          if (contentCursorPosition !== null) {
            editor.commands.setTextSelection(contentCursorPosition);
          }
          
          // Use the custom insertVariable command
          (editor.commands as any).insertVariable(variable);
        } catch (error) {
          console.error('Error inserting via editor commands:', error);
        }
      } else {
        // Fallback: update via state, preserving HTML structure
        // Parse the HTML content
        const parser = new DOMParser();
        const doc = parser.parseFromString(content || '<p></p>', 'text/html');
        
        // Create the variable node as a span with data-variable attribute
        const variableSpan = doc.createElement('span');
        variableSpan.setAttribute('data-variable', variable);
        variableSpan.className = 'variable-node';
        
        // Get all text nodes and their positions
        let currentPos = 0;
        let targetNode: Node | null = null;
        let offsetInNode = 0;
        const targetPos = contentCursorPosition ?? 0;
        
        // Walk through all text nodes to find the insertion point
        const walker = document.createTreeWalker(
          doc.body,
          NodeFilter.SHOW_TEXT,
          null
        );
        
        let node: Node | null;
        while ((node = walker.nextNode())) {
          const textLength = node.textContent?.length || 0;
          if (currentPos + textLength >= targetPos) {
            targetNode = node;
            offsetInNode = targetPos - currentPos;
            break;
          }
          currentPos += textLength;
        }
        
        // Insert the variable at the found position
        if (targetNode && targetNode.parentNode) {
          const text = targetNode.textContent || '';
          
          // Split the text node at the insertion point
          if (offsetInNode > 0) {
            const beforeText = doc.createTextNode(text.substring(0, offsetInNode));
            const afterText = doc.createTextNode(text.substring(offsetInNode));
            
            targetNode.parentNode.insertBefore(beforeText, targetNode);
            targetNode.parentNode.insertBefore(variableSpan, targetNode);
            targetNode.parentNode.insertBefore(afterText, targetNode);
            targetNode.parentNode.removeChild(targetNode);
          } else {
            targetNode.parentNode.insertBefore(variableSpan, targetNode);
          }
        } else {
          // Fallback: append to the last paragraph or create new paragraph
          const lastP = doc.body.querySelector('p:last-child');
          if (lastP) {
            lastP.appendChild(variableSpan);
          } else {
            const p = doc.createElement('p');
            p.appendChild(variableSpan);
            doc.body.appendChild(p);
          }
        }
        
        // Get the updated HTML
        const newContent = doc.body.innerHTML;
        setContent(newContent);
      }
    }
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a template name');
      return;
    }

    const subjectHtml = subjectEditor?.getHTML() || '';
    const subjectContent = convertVariableNodesToText(subjectHtml);
    const bodyContent = content || contentEditorRef.current?.getHTML() || '';

    if (!subjectContent.trim()) {
      alert('Please enter a subject line');
      return;
    }

    if (!bodyContent.trim()) {
      alert('Please enter template content');
      return;
    }

    if (isEditMode && template) {
      const templateData: Template = {
        ...template,
        name,
        subject: subjectContent,
        content: bodyContent,
        type,
        attachInvoiceCopies,
      };

      setIsSaving(true);
      updateTemplateMutation.mutate(templateData, {
        onSuccess: (apiResponse) => {
          setIsSaving(false);
          const savedTemplate = mapTemplateDetailApiToUi(apiResponse);
          navigate('/settings/templates', { state: { savedTemplate, mode: 'edit' } });
        },
        onError: (error) => {
          setIsSaving(false);
          alert(`Failed to save template: ${error.message}`);
        },
      });
    } else {
      const templateData: Template = {
        id: '',
        name,
        subject: subjectContent,
        content: bodyContent,
        type,
        isDefault: false,
        category: [],
        variables: [],
        lastModified: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        createdBy: '',
        attachInvoiceCopies,
      };

      setIsSaving(true);
      createTemplateMutation.mutate(templateData, {
        onSuccess: (apiTemplate) => {
          setIsSaving(false);
          const savedTemplate = mapTemplateDetailApiToUi(apiTemplate);
          navigate('/settings/templates', { state: { savedTemplate, mode: 'create' } });
        },
        onError: (error) => {
          setIsSaving(false);
          alert(`Failed to create template: ${error.message}`);
        },
      });
    }
  };

  const handleCancel = () => {
    // Get current editor content (HTML for both subject and content)
    const currentSubject = subjectEditor?.getHTML() || subject;
    const currentContent = contentEditorRef.current?.getHTML() || content;
    
    // Normalize HTML for comparison (remove extra whitespace, etc.)
    const normalizeHtml = (html: string) => {
      return html.replace(/\s+/g, ' ').trim();
    };
    
    const normalizedCurrentSubject = normalizeHtml(currentSubject);
    const normalizedInitialSubject = normalizeHtml(initialSubject);
    const normalizedCurrentContent = normalizeHtml(currentContent);
    const normalizedInitialContent = normalizeHtml(initialContent);
    
    // Check if there are actual changes
    const hasChanges = 
      name.trim() !== initialName.trim() ||
      normalizedCurrentSubject !== normalizedInitialSubject ||
      normalizedCurrentContent !== normalizedInitialContent ||
      attachInvoiceCopies !== initialAttachInvoiceCopies;

    if (hasChanges) {
      setShowCancelDialog(true);
    } else {
      navigate('/settings/templates');
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelDialog(false);
    navigate('/settings/templates');
  };

  const handlePreview = () => {
    // Use the content state directly (it's already HTML from the editor)
    // Prioritize the state variables as they contain the actual content
    const previewContentRaw = content || contentEditorRef.current?.getHTML() || '';
    const previewSubjectRaw = subject || subjectEditor?.getHTML() || '';
    
    // Sample data for variable replacement
    const sampleData: Record<string, string> = {
      // Customer Information (underscore format)
      '{{customer_name}}': 'John Smith',
      '{{customer_email}}': 'john.smith@acme.com',
      '{{customer_phone}}': '+1 (555) 123-4567',
      '{{account_number}}': 'ACC-12345',
      '{{contact_name}}': 'Jane Doe',
      '{{contact_email}}': 'jane.doe@acme.com',
      '{{contact_phone}}': '+1 (555) 987-6543',
      '{{account_balance}}': '$15,750.00',
      '{{overdue_balance}}': '$5,250.00',
      '{{credit_limit}}': '$50,000.00',
      
      // Customer Information (dot notation format)
      '{{customer.name}}': 'John Smith',
      '{{customer.email}}': 'john.smith@acme.com',
      '{{customer.phone}}': '+1 (555) 123-4567',
      '{{customer.accountNumber}}': 'ACC-12345',
      '{{customer.contactName}}': 'Jane Doe',
      '{{customer.contactEmail}}': 'jane.doe@acme.com',
      '{{customer.contactPhone}}': '+1 (555) 987-6543',
      
      // Account Information (dot notation format)
      '{{account.balance}}': '$15,750.00',
      '{{account.overdueBalance}}': '$5,250.00',
      '{{account.creditLimit}}': '$50,000.00',
      '{{account.number}}': 'ACC-12345',
      
      // Transaction Details (underscore format)
      '{{reference_number}}': 'INV-2024-001',
      '{{transaction_date}}': 'January 15, 2024',
      '{{due_date}}': 'February 15, 2024',
      '{{gross}}': '$5,250.00',
      '{{amount_due}}': '$5,250.00',
      '{{days_overdue}}': '15',
      '{{last_payment_date}}': 'December 20, 2023',
      '{{case_amount}}': '$15,750.00',
      '{{case_transactions}}': `<table style="width: 100% !important; border-collapse: collapse !important; margin: 16px 0 !important; font-size: 14px !important; line-height: 1.5 !important; border: 1px solid #dee2e6 !important;">
        <thead>
          <tr style="background-color: #f8f9fa !important; border-bottom: 2px solid #dee2e6 !important;">
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Reference</th>
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Date</th>
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Due Date</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Gross Value</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Outstanding</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2024-001</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 15, 2024</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Feb 15, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$5,250.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$5,250.00</td>
          </tr>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2023-089</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Dec 10, 2023</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 10, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$10,500.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$10,500.00</td>
          </tr>
        </tbody>
      </table>`,
      '{{outstanding_transactions}}': `<table style="width: 100% !important; border-collapse: collapse !important; margin: 16px 0 !important; font-size: 14px !important; line-height: 1.5 !important; border: 1px solid #dee2e6 !important;">
        <thead>
          <tr style="background-color: #f8f9fa !important; border-bottom: 2px solid #dee2e6 !important;">
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Reference</th>
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Date</th>
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Due Date</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Gross Value</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Outstanding</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2024-003</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 20, 2024</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Feb 20, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$3,200.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$3,200.00</td>
          </tr>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2024-002</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 18, 2024</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Feb 18, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$7,800.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$7,800.00</td>
          </tr>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2024-001</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 15, 2024</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Feb 15, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$5,250.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$5,250.00</td>
          </tr>
        </tbody>
      </table>`,
      '{{overdue_transactions}}': `<table style="width: 100% !important; border-collapse: collapse !important; margin: 16px 0 !important; font-size: 14px !important; line-height: 1.5 !important; border: 1px solid #dee2e6 !important;">
        <thead>
          <tr style="background-color: #f8f9fa !important; border-bottom: 2px solid #dee2e6 !important;">
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Reference</th>
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Date</th>
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Due Date</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Gross Value</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Outstanding</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2024-001</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 15, 2024</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Feb 15, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$5,250.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$5,250.00</td>
          </tr>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2023-089</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Dec 10, 2023</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 10, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$10,500.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">$10,500.00</td>
          </tr>
        </tbody>
      </table>`,
      
      // Transaction Details (dot notation format)
      '{{transaction.referenceNumber}}': 'INV-2024-001',
      '{{transaction.date}}': 'January 15, 2024',
      '{{transaction.dueDate}}': 'February 15, 2024',
      '{{transaction.gross}}': '$5,250.00',
      '{{transaction.amountDue}}': '$5,250.00',
      '{{transaction.daysOverdue}}': '15',
      '{{transaction.lastPaymentDate}}': 'December 20, 2023',
      
      // Company Information (underscore format)
      '{{company_name}}': 'Acme Corporation',
      '{{company_email}}': 'info@acme.com',
      '{{company_phone}}': '+1 (555) 000-0000',
      '{{support_email}}': 'support@acme.com',
      '{{credit_controller}}': 'Michael Johnson',
      '{{credit_controller_email}}': 'michael.johnson@acme.com',
      
      // Company Information (dot notation format)
      '{{company.name}}': 'Acme Corporation',
      '{{company.email}}': 'info@acme.com',
      '{{company.phone}}': '+1 (555) 000-0000',
      '{{company.supportEmail}}': 'support@acme.com',
      '{{company.creditController}}': 'Michael Johnson',
      '{{company.creditControllerEmail}}': 'michael.johnson@acme.com',
      
      // Payment Information (underscore format)
      '{{payment_link}}': 'https://payment.example.com/pay/INV-2024-001',
      '{{payment_methods}}': 'Bank Transfer, Credit Card, PayPal',
      
      // Payment Information (dot notation format)
      '{{payment.link}}': 'https://payment.example.com/pay/INV-2024-001',
      '{{payment.methods}}': 'Bank Transfer, Credit Card, PayPal',
    };

    // Helper function to replace variables in HTML
    const replaceVariablesInHtml = (html: string): string => {
      if (!html || html.trim() === '') {
        return html;
      }
      
      // Create a temporary div to work with the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Find all variable spans
      const variableSpans = tempDiv.querySelectorAll('span[data-variable]');
      
      variableSpans.forEach((span) => {
        const variable = span.getAttribute('data-variable');
        if (variable && sampleData[variable]) {
          // Check if the variable value contains HTML (like case_transactions table)
          if (sampleData[variable].includes('<')) {
            // For HTML content, create a temporary container and set innerHTML
            const htmlContainer = document.createElement('div');
            htmlContainer.innerHTML = sampleData[variable];
            // Replace the span with all children of the container
            while (htmlContainer.firstChild) {
              span.parentNode?.insertBefore(htmlContainer.firstChild, span);
            }
            span.parentNode?.removeChild(span);
          } else {
            // For plain text, create a strong element with the sample value
            const strong = document.createElement('strong');
            strong.textContent = sampleData[variable];
            // Replace the span with the strong element
            span.parentNode?.replaceChild(strong, span);
          }
        }
      });
      
      // Return the modified HTML
      return tempDiv.innerHTML;
    };

    // Replace variables with sample data for preview
    const previewContent = replaceVariablesInHtml(previewContentRaw);
    const previewSubject = replaceVariablesInHtml(previewSubjectRaw);
    
    console.log('Preview Content Raw:', previewContentRaw);
    console.log('Preview Content Processed:', previewContent);
    console.log('Preview Subject Raw:', previewSubjectRaw);
    console.log('Preview Subject Processed:', previewSubject);

    // Store as JSON string with both subject and content
    const previewData = JSON.stringify({
      subject: previewSubject,
      content: previewContent
    });

    console.log('Preview Data JSON:', previewData);

    setPreviewHtml(previewData);
    setShowPreview(true);
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="border-b bg-background">
        <div className="container mx-auto py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCancel}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold">
                  {isEditMode ? 'Edit Template' : 'Create Template'}
                </h1>
                <p className="text-muted-foreground">
                  {isEditMode ? 'Modify your template' : 'Create a new template'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="outline" onClick={handlePreview}>
                Preview
              </Button>
              <Button onClick={handleSave} disabled={isSaving || isLoadingTemplate}>
                {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEditMode ? 'Save Changes' : 'Create Template'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="flex-1 overflow-hidden">
        <div className="container mx-auto h-full">
          {isEditMode && isLoadingTemplate ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Loading template...</p>
              </div>
            </div>
          ) : (
          <div className="flex flex-row gap-3 h-full py-6">
            {/* Left Column - Template Details */}
            <div className="flex-[7] overflow-y-auto pr-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
              {/* Template Details Card */}
              <Card className="h-fit">
                <CardHeader>
                  <CardTitle>Template Details</CardTitle>
                  <CardDescription>
                    {type === 'statement' && 'Create an email statement template with rich text formatting'}
                    {type === 'email' && 'Create an email template with rich text formatting'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Template Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter template name"
                    />
                  </div>

                  {/* Subject Line */}
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject Line</Label>
                    {subjectEditor ? (
                      <div 
                        className={`border rounded-md transition-all relative ${
                          isSubjectDragOver ? 'border-primary border-2 ring-2 ring-primary/20' : ''
                        }`}
                        onDragEnter={(e) => {
                          const types = e.dataTransfer?.types || [];
                          if (types.includes('application/x-template-variable')) {
                            e.preventDefault();
                            setIsSubjectDragOver(true);
                          }
                        }}
                        onDragLeave={(e) => {
                          // Check if we're leaving the editor container (not just entering a child)
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX;
                          const y = e.clientY;
                          
                          // Only clear if mouse is actually outside the container bounds
                          if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
                            setIsSubjectDragOver(false);
                          }
                        }}
                        onDragOver={(e) => {
                          const types = e.dataTransfer?.types || [];
                          if (types.includes('application/x-template-variable')) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'copy';
                          }
                        }}
                        onDrop={() => {
                          // Always clear the drag over state when dropping
                          setIsSubjectDragOver(false);
                        }}
                      >
                        <EditorContent 
                          editor={subjectEditor} 
                          className="prose prose-sm max-w-none p-3 min-h-[44px] focus-within:outline-none"
                        />
                        {isSubjectDragOver && (
                          <div className="absolute inset-0 bg-primary/5 pointer-events-none flex items-center justify-center">
                            <div className="bg-background border-2 border-primary rounded-lg px-3 py-1.5 shadow-lg">
                              <p className="text-xs font-medium text-primary">Drop variable here</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="border rounded-md p-3 min-h-[44px] text-muted-foreground">
                        Loading editor...
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="space-y-2">
                    <Label htmlFor="content">Content</Label>
                    <RichTextEditor
                      content={content}
                      onChange={setContent}
                      onEditorReady={handleContentEditorReady}
                      onFocus={handleContentEditorFocus}
                      onSelectionUpdate={handleContentSelectionUpdate}
                      onBlur={handleContentEditorBlur}
                      placeholder="Enter template content..."
                    />
                  </div>

                  <div className="flex items-start space-x-3 pt-2">
                    <Checkbox
                      id="attachInvoiceCopies"
                      checked={attachInvoiceCopies}
                      onCheckedChange={(checked) => setAttachInvoiceCopies(checked === true)}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label
                        htmlFor="attachInvoiceCopies"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        Attach invoice copies to emails
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically attach invoice documents when sending emails using this template
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Variables Panel */}
            <div className="flex-[3] overflow-y-auto pl-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
              <Card className="h-fit">
                <CardHeader>
                  <CardTitle>Available Variables</CardTitle>
                </CardHeader>
                <CardContent>
                  <TemplateVariablesPanel onInsertVariable={handleInsertVariable} />
                </CardContent>
              </Card>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Enhanced Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={(open) => {
        setShowPreview(open);
        if (!open) {
          // Clear preview data when dialog closes
          setPreviewHtml('');
        }
      }}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <Eye className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-semibold">Template Preview</DialogTitle>
                  <DialogDescription className="text-sm mt-1">
                    Preview how your template will appear with sample data
                  </DialogDescription>
                </div>
              </div>
              <Badge variant="secondary" className="flex items-center gap-1.5 px-3 py-1">
                {type === 'email' ? (
                  <>
                    <Mail className="h-3.5 w-3.5" />
                    <span>Email Template</span>
                  </>
                ) : (
                  <>
                    <Mail className="h-3.5 w-3.5" />
                    <span>Statement - Email</span>
                  </>
                )}
              </Badge>
            </div>
          </DialogHeader>
          
          <Separator />
          
          <ScrollArea className="flex-1 px-6">
            <div className="py-6">
              {(() => {
                try {
                  if (!previewHtml || previewHtml.trim() === '') {
                    return (
                      <div className="text-center text-muted-foreground py-8">
                        No preview data available
                      </div>
                    );
                  }
                  
                  const previewData = JSON.parse(previewHtml);
                  const sanitizedSubject = DOMPurify.sanitize(previewData.subject, {
                    ALLOWED_TAGS: ['strong', 'b', 'em', 'i', 'u', 'span'],
                    ALLOWED_ATTR: [],
                  });
                  const sanitizedContent = DOMPurify.sanitize(previewData.content, {
                    ALLOWED_TAGS: ['div', 'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'blockquote', 'pre', 'code', 'hr'],
                    ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
                  });
                  
                  return (
                    <>
                      {type === 'email' && sanitizedSubject && (
                        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
                          <div className="flex items-start gap-3">
                            <Mail className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-muted-foreground mb-1">Subject Line</div>
                              <div 
                                className="text-base font-medium text-foreground break-words"
                                dangerouslySetInnerHTML={{ __html: sanitizedSubject }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
                        <div className="bg-muted/30 px-4 py-2 border-b border-border">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Template Content
                          </div>
                        </div>
                        <div className="p-6">
                          <div 
                            className="prose prose-sm max-w-none
                              prose-headings:text-foreground prose-headings:font-semibold
                              prose-p:text-foreground prose-p:leading-relaxed
                              prose-strong:text-foreground prose-strong:font-semibold
                              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                              prose-ul:text-foreground prose-ol:text-foreground
                              prose-li:text-foreground prose-li:marker:text-muted-foreground
                              prose-table:border-collapse prose-table:w-full
                              prose-th:bg-muted/50 prose-th:border prose-th:border-border prose-th:px-4 prose-th:py-2 prose-th:text-left prose-th:font-semibold
                              prose-td:border prose-td:border-border prose-td:px-4 prose-td:py-2
                              prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground
                              prose-code:text-foreground prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                              prose-pre:bg-muted prose-pre:text-foreground"
                            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                          />
                        </div>
                      </div>
                    </>
                  );
                } catch (error) {
                  console.error('Error parsing preview data:', error);
                  return (
                    <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
                      <p className="font-medium">Error displaying preview</p>
                      <p className="text-sm mt-1">Unable to parse preview data. Please try again.</p>
                    </div>
                  );
                }
              })()}
              
              <div className="mt-6 p-4 bg-accent/50 rounded-lg border border-accent-foreground/10">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent flex-shrink-0">
                    <span className="text-xs font-semibold text-accent-foreground">ℹ</span>
                  </div>
                  <div className="flex-1 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Preview Information</p>
                    <p>This preview shows your template with sample data. Variable placeholders have been replaced with example values to demonstrate how the final output will appear to recipients.</p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
          
          <Separator />
          
          <DialogFooter className="px-6 py-4">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel? Any unsaved changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
